/**
 * Servicio de logros por acción ("trofeos").
 *
 * Detecta acciones repetidas del estudiante (saludar, agradecer, preguntar…),
 * acumula contadores PERSISTENTES y desbloquea trofeos al alcanzar el umbral.
 *
 * Persistencia resiliente:
 *  - Primario: Supabase `user_summary.action_progress` (jsonb), vía anon key
 *    (igual que el resto de user_summary).
 *  - Fallback: localStorage por email, si la columna aún no existe o falla la
 *    red. Así funciona HOY en dev sin depender de un ALTER TABLE; cuando la
 *    columna esté, sube a Supabase automáticamente. DDL en
 *    scripts/sql/galia_action_progress.sql.
 */

import supabaseService from './supabaseService';
import { isKioskMode } from '../utils/featureFlags';
import {
  ACTION_ACHIEVEMENTS,
  ACHIEVEMENTS_BY_ID,
  ActionAchievement,
  detectActions,
} from '../config/actionAchievements';
import {
  LevelTrophy,
  TROPHIES_BY_ID,
  isTrophyComplete,
  trophiesForLevel,
  trophyTarget,
} from '../config/trophies';
import { MCERLevel } from '../types';

export interface ActionProgress {
  /** id de acción → veces realizada. */
  counts: Record<string, number>;
  /** ids de logros ya desbloqueados (legacy por regex Y trofeos por nivel). */
  unlocked: string[];
  /**
   * Trofeos por nivel (spec Excel): id de trofeo → casilla → veces reconocida
   * por el LLM. Ej.: trophyCounts["a1_etre"]["je"] = 1. Ausente en progreso
   * anterior a 2026-08-25 (se inicializa perezosamente).
   */
  trophyCounts?: Record<string, Record<string, number>>;
  /**
   * Nivel base (A1…C2) de cada casilla de los trofeos académicos (casilla =
   * id del curso AEC). galia-ws lo manda con cada reconocimiento desde el
   * 30/09/2026; el cofre agrupa con esto los trofeos académicos por nivel
   * (mesa del 29/09/2026: « los trofeos académicos son por nivel »). Las
   * casillas anteriores a esa fecha no tienen nivel y se muestran en la
   * pestaña del nivel actual del estudiante.
   */
  aecLevels?: Record<string, string>;
  /**
   * Fecha ISO en que el estudiante vio la bienvenida de primera conexión.
   * Viaja con el progreso (no solo en localStorage) por la misma razón que
   * `seen`: si no, reaparece en la PWA instalada, en incógnito y en cada
   * perfil del navegador. Va dentro de este JSON porque es la única fila por
   * usuario que ya existe — evita un DDL nuevo en Supabase.
   */
  welcomeSeenAt?: string;
  /**
   * Trofeos que el estudiante YA VIO en el cofre (badge del botón copa).
   * Viaja con el progreso —y no solo en localStorage— desde el 17/09/2026:
   * ver el bloque « Trofeos vistos » más abajo.
   */
  seen?: string[];
}

/** Reconocimiento emitido por galia-ws (marcador [[trophee:ID:CASILLA]]). */
export interface TrophyRecognition {
  id: string;
  slot: string;
  /** Solo trofeos académicos: nivel base del curso de la casilla (A1…C2). */
  level?: string;
}

const STORAGE_PREFIX = 'galia_action_progress:';
export const PROGRESS_UPDATED_EVENT = 'galia:progress-updated';
export const ACHIEVEMENT_UNLOCKED_EVENT = 'galia:achievement-unlocked';

function emptyProgress(): ActionProgress {
  return { counts: {}, unlocked: [], trophyCounts: {}, aecLevels: {}, seen: [] };
}

/** LevelTrophy → forma de ActionAchievement (para el toast y la cola de UI). */
function trophyAsAchievement(t: LevelTrophy): ActionAchievement {
  return {
    id: t.id,
    emoji: t.emoji,
    title: t.title,
    titleFr: t.titleFr,
    // El texto que lee el estudiante es la COLUMNA F del Excel (descFr), no la
    // paráfrasis en español — pedido de Santiago 26/08.
    blurb: t.descFr,
    hint: t.hint,
    threshold: Math.max(trophyTarget(t), 1),
    match: [],
  };
}

/** Preview solo-dev habilitado igual que el modo testing (nunca en prod). */
function isPreviewAllowed(): boolean {
  const env = import.meta.env.VITE_TESTING_PHASE;
  if (env !== undefined) return env === 'true' || (env as unknown) === true;
  return import.meta.env.DEV === true;
}

/** ?ach_preview=greeting,thanks → simula esos logros desbloqueados (solo dev). */
function readAchPreview(): Set<string> {
  if (typeof window === 'undefined' || !isPreviewAllowed()) return new Set();
  const raw = new URLSearchParams(window.location.search).get('ach_preview');
  if (!raw) return new Set();
  if (raw === 'all') {
    return new Set([
      ...ACTION_ACHIEVEMENTS.map((a) => a.id),
      ...Object.keys(TROPHIES_BY_ID),
    ]);
  }
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter((id) => ACHIEVEMENTS_BY_ID[id] || TROPHIES_BY_ID[id])
  );
}

/** ?ach_reset=1 → borra el progreso de logros (solo dev, una vez por carga). */
function shouldReset(): boolean {
  if (typeof window === 'undefined' || !isPreviewAllowed()) return false;
  return new URLSearchParams(window.location.search).has('ach_reset');
}

class AchievementService {
  /** Cache en memoria por email para no releer en cada mensaje. */
  private cache = new Map<string, ActionProgress>();
  /** Emails ya reseteados en esta carga (para no borrar en cada mensaje). */
  private resetDone = new Set<string>();

  private storageKey(email: string): string {
    return `${STORAGE_PREFIX}${email.toLowerCase()}`;
  }

  private readLocal(email: string): ActionProgress | null {
    try {
      const raw = localStorage.getItem(this.storageKey(email));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return {
        counts: parsed.counts || {},
        unlocked: parsed.unlocked || [],
        trophyCounts: parsed.trophyCounts || {},
        aecLevels: parsed.aecLevels || {},
        seen: parsed.seen || [],
        welcomeSeenAt: parsed.welcomeSeenAt,
      };
    } catch {
      return null;
    }
  }

  private writeLocal(email: string, progress: ActionProgress): void {
    try {
      localStorage.setItem(this.storageKey(email), JSON.stringify(progress));
    } catch {
      /* ignore quota/private-mode errors */
    }
  }

  /** Carga (una vez por email) desde Supabase con fallback a localStorage. */
  private async ensureLoaded(email: string): Promise<ActionProgress> {
    // Reset solo-dev: borra una vez por carga antes de cachear.
    if (shouldReset() && !this.resetDone.has(email)) {
      this.resetDone.add(email);
      const fresh = emptyProgress();
      this.cache.set(email, fresh);
      await this.persist(email, fresh);
      return fresh;
    }

    const cached = this.cache.get(email);
    if (cached) return cached;

    let progress: ActionProgress | null = null;
    try {
      const { data } = await supabaseService
        .getClient()
        .from('user_summary')
        .select('action_progress')
        .eq('email', email)
        .single();
      const remote = (data as any)?.action_progress;
      if (remote && typeof remote === 'object') {
        progress = {
          counts: remote.counts || {},
          unlocked: remote.unlocked || [],
          trophyCounts: remote.trophyCounts || {},
          aecLevels: remote.aecLevels || {},
          seen: remote.seen || [],
          welcomeSeenAt: remote.welcomeSeenAt,
        };
      }
    } catch {
      /* columna ausente o red caída → fallback */
    }

    if (!progress) progress = this.readLocal(email) || emptyProgress();
    this.cache.set(email, progress);
    return progress;
  }

  /** Cola de escrituras a Supabase por email (ver persist). */
  private persistChain = new Map<string, Promise<void>>();

  /**
   * Persiste en Supabase (si se puede) y siempre en localStorage como caché.
   *
   * Las escrituras van EN FILA por email y cada una sube el estado MÁS RECIENTE
   * de la caché (no el que había al encolarse). Antes cada reconocimiento
   * lanzaba su propio UPDATE del objeto completo en paralelo: si dos se
   * cruzaban (regex local + marcadores del LLM del mismo turno), el más viejo
   * podía aterrizar último y pisar al nuevo. Prueba del 11/09/2026: el toast de
   * "Encore un peu ?" y el de plata A2 salieron, localStorage los tenía, pero
   * Supabase se quedó con la versión anterior — y al recargar manda Supabase.
   */
  private persist(email: string, progress: ActionProgress): Promise<void> {
    this.writeLocal(email, progress);
    const prev = this.persistChain.get(email) || Promise.resolve();
    const next = prev.then(() => this.writeRemote(email, progress));
    this.persistChain.set(email, next);
    return next;
  }

  private async writeRemote(email: string, fallback: ActionProgress): Promise<void> {
    const latest = this.cache.get(email) || fallback;
    // Copia: el objeto de la caché se sigue mutando mientras viaja el request.
    const snapshot: ActionProgress = JSON.parse(JSON.stringify(latest));
    try {
      const { error } = await supabaseService
        .getClient()
        .from('user_summary')
        .update({ action_progress: snapshot, last_updated: new Date().toISOString() })
        .eq('email', email);
      if (error) throw error;
    } catch {
      /* columna ausente / sin fila / red → ya quedó en localStorage */
    }
  }

  /**
   * Procesa un mensaje del estudiante: cuenta acciones y devuelve los trofeos
   * recién desbloqueados (para mostrar el toast). No relanza trofeos ya obtenidos.
   */
  async processMessage(email: string, text: string): Promise<ActionAchievement[]> {
    if (!email) return [];
    // El catálogo legacy por regex SOLO vive en el kiosco (donde alimenta los 3
    // trofeos de recepción). Fuera del kiosco los trofeos son los del Excel de
    // Santiago; si esto siguiera activo, los logros viejos ("introduce",
    // "question"…) seguirían desbloqueándose encima — fue exactamente lo que
    // le pasó a Santiago el 26/08 ("me avisó de trofeos anteriores").
    if (!isKioskMode()) return [];
    const actions = detectActions(text);
    if (actions.length === 0) return [];

    const progress = await this.ensureLoaded(email);
    const newlyUnlocked: ActionAchievement[] = [];

    for (const id of actions) {
      const def = ACHIEVEMENTS_BY_ID[id];
      if (!def) continue;
      const already = progress.unlocked.includes(id);
      const next = Math.min((progress.counts[id] || 0) + 1, def.threshold);
      progress.counts[id] = next;
      if (!already && next >= def.threshold) {
        progress.unlocked.push(id);
        newlyUnlocked.push(def);
      }
    }

    await this.persist(email, progress);

    if (newlyUnlocked.length > 0) {
      // Refrescar el paisaje (piezas ligadas a logros) y notificar a la UI.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(PROGRESS_UPDATED_EVENT));
        for (const a of newlyUnlocked) {
          window.dispatchEvent(new CustomEvent(ACHIEVEMENT_UNLOCKED_EVENT, { detail: a }));
        }
      }
    }
    return newlyUnlocked;
  }

  /**
   * El LLM a veces emite la casilla por pronombre suelto ("elle", "il", "ils")
   * en vez del nombre del catálogo ("il_elle", "ils_elles"). Antes esas
   * casillas se descartaban en silencio: en la prueba del 11/09/2026 las 3.ª
   * personas de b1_subjonctif y b1_conditionnel no sumaban nunca (Galia emitía
   * [[trophee:b1_subjonctif:elle]]). Se traducen al nombre del catálogo.
   */
  private static readonly SLOT_ALIASES: Record<string, string> = {
    il: 'il_elle', elle: 'il_elle', on: 'il_elle', il_elle_on: 'il_elle',
    ils: 'ils_elles', elles: 'ils_elles',
  };

  private static normalizeSlot(counters: Record<string, number>, slot: string): string {
    if (slot in counters) return slot;
    const alias = AchievementService.SLOT_ALIASES[slot];
    return alias && alias in counters ? alias : slot;
  }

  /**
   * Procesa los reconocimientos de trofeos que emitió galia-ws para el último
   * turno (marcadores [[trophee:ID:CASILLA]] → mensaje __TROPHEES__). Suma los
   * contadores por casilla (con tope en la meta), desbloquea trofeos completos
   * y el de plata del nivel cuando los demás están, y devuelve los recién
   * desbloqueados en forma de ActionAchievement (para el toast).
   */
  async processRecognitions(
    email: string,
    recognitions: TrophyRecognition[]
  ): Promise<ActionAchievement[]> {
    if (!email || recognitions.length === 0) return [];

    const progress = await this.ensureLoaded(email);
    if (!progress.trophyCounts) progress.trophyCounts = {};
    const newlyUnlocked: ActionAchievement[] = [];
    let changed = false;

    const touchedLevels = new Set<string>();
    const aliasedSeen = new Set<string>();
    for (const rec of recognitions) {
      const def = TROPHIES_BY_ID[rec.id];
      // Ignorar ids desconocidos o casillas que no existen en la spec (el LLM
      // podría alucinar un marcador; solo cuentan los del catálogo). Los
      // trofeos `dynamic` (académicos AEC) aceptan cualquier casilla — una por
      // curso, con tope 1, así reenviarlos cada sesión no infla el contador.
      if (!def || def.meta) continue;
      const slot = def.dynamic ? rec.slot : AchievementService.normalizeSlot(def.counters, rec.slot);
      if (!def.dynamic && !(slot in def.counters)) continue;
      // "il" y "elle" del mismo turno caen en la misma casilla: una sola vez.
      if (slot !== rec.slot) {
        const key = `${def.id}:${slot}`;
        if (aliasedSeen.has(key)) continue;
        aliasedSeen.add(key);
      }
      const target = def.dynamic ? 1 : def.counters[slot];
      const slots = (progress.trophyCounts[def.id] ||= {});
      // Nivel del curso de la casilla académica: se anota aunque la casilla ya
      // esté llena (así las casillas viejas, sin nivel, lo adquieren).
      if (def.dynamic && rec.level) {
        const levels = (progress.aecLevels ||= {});
        if (levels[slot] !== rec.level) {
          levels[slot] = rec.level;
          changed = true;
        }
      }
      const current = slots[slot] || 0;
      if (current >= target) continue; // casilla ya llena
      slots[slot] = current + 1;
      changed = true;
      touchedLevels.add(def.level);

      if (!progress.unlocked.includes(def.id) && isTrophyComplete(def, slots)) {
        progress.unlocked.push(def.id);
        newlyUnlocked.push(trophyAsAchievement(def));
      }
    }

    // Trofeo de plata del nivel: se desbloquea cuando TODOS los bronce están.
    for (const level of touchedLevels) {
      for (const def of trophiesForLevel(level as MCERLevel)) {
        if (!def.meta || progress.unlocked.includes(def.id)) continue;
        const bronzes = trophiesForLevel(def.level as MCERLevel).filter((t) => !t.meta);
        if (bronzes.every((t) => progress.unlocked.includes(t.id))) {
          progress.unlocked.push(def.id);
          newlyUnlocked.push(trophyAsAchievement(def));
          changed = true;
        }
      }
    }

    if (!changed) return [];
    await this.persist(email, progress);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PROGRESS_UPDATED_EVENT));
      for (const a of newlyUnlocked) {
        window.dispatchEvent(new CustomEvent(ACHIEVEMENT_UNLOCKED_EVENT, { detail: a }));
      }
    }
    return newlyUnlocked;
  }

  /** Conjunto de logros desbloqueados (incluye preview solo-dev). */
  async getUnlockedAchievements(email?: string | null): Promise<Set<string>> {
    const preview = readAchPreview();
    if (!email) return preview;
    const progress = await this.ensureLoaded(email);
    return new Set([...progress.unlocked, ...preview]);
  }

  /** Progreso de contadores (para futuras vistas de "casi lo logras"). */
  async getProgress(email: string): Promise<ActionProgress> {
    return this.ensureLoaded(email);
  }

  /* ── Trofeos "vistos" (badge del botón copa) ──
   * El badge muestra solo los trofeos NUEVOS desde la última vez que el
   * estudiante abrió el cofre; al abrirlo se marca todo como visto y el badge
   * desaparece (feedback Santiago 2026-07-09: el conteo total parecía una
   * notificación que nunca se iba).
   *
   * Hasta el 17/09/2026 esto vivía SOLO en localStorage ("es estado de UI,
   * localStorage por email basta"). Esa suposición era falsa: el progreso
   * (`unlocked`) viene de Supabase y viaja entre contextos, pero `seen` no —
   * la PWA instalada, una ventana de incógnito y cada perfil del navegador
   * tienen su propio localStorage. Al entrar por otra vía el conjunto de
   * vistos llegaba vacío y los trofeos ya conocidos volvían a contarse como
   * nuevos (reunión 15/09/2026: a Santiago le reaparecían las 13
   * notificaciones en cada conexión). Ahora `seen` viaja DENTRO del progreso
   * (Supabase + localStorage como caché) y se une con lo que hubiera en local,
   * para no perder lo ya visto de quien venía de la versión anterior. */

  private seenKey(email?: string | null): string {
    return `galia_trophies_seen:${(email || 'anonymous').toLowerCase()}`;
  }

  /** Vistos guardados en este navegador (versión anterior y respaldo local). */
  private readLocalSeen(email?: string | null): string[] {
    try {
      const raw = localStorage.getItem(this.seenKey(email));
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }

  private writeLocalSeen(email: string | null | undefined, seen: string[]): void {
    try {
      localStorage.setItem(this.seenKey(email), JSON.stringify(seen));
    } catch {
      /* ignore quota/private-mode errors */
    }
  }

  /** Unión de los vistos del progreso (Supabase) y los de este navegador. */
  private async readSeen(email?: string | null): Promise<Set<string>> {
    const local = this.readLocalSeen(email);
    if (!email) return new Set(local);
    const progress = await this.ensureLoaded(email);
    return new Set([...(progress.seen || []), ...local]);
  }

  /** Nº de trofeos desbloqueados que el estudiante aún no ha visto en el cofre. */
  async getUnseenCount(email?: string | null): Promise<number> {
    const unlocked = await this.getUnlockedAchievements(email);
    const seen = await this.readSeen(email);
    let count = 0;
    unlocked.forEach((id) => {
      if (!seen.has(id)) count++;
    });
    return count;
  }

  /* ── Bienvenida de primera conexión (informe UX de Melissa Amaya, 11/09/2026,
   * hallazgos 1/4/7; pedida por Santiago en la reunión del 15/09) ── */

  private welcomeKey(email?: string | null): string {
    return `galia_welcome_seen:${(email || 'anonymous').toLowerCase()}`;
  }

  /** ¿Ya vio la bienvenida? Cualquiera de las dos fuentes basta. */
  async hasSeenWelcome(email?: string | null): Promise<boolean> {
    let local = false;
    try {
      local = localStorage.getItem(this.welcomeKey(email)) !== null;
    } catch {
      /* private mode */
    }
    if (local || !email) return local;
    const progress = await this.ensureLoaded(email);
    return Boolean(progress.welcomeSeenAt);
  }

  async markWelcomeSeen(email?: string | null): Promise<void> {
    const now = new Date().toISOString();
    try {
      localStorage.setItem(this.welcomeKey(email), now);
    } catch {
      /* private mode */
    }
    if (!email) return;
    const progress = await this.ensureLoaded(email);
    if (progress.welcomeSeenAt) return;
    progress.welcomeSeenAt = now;
    await this.persist(email, progress);
  }

  /** Marca todos los trofeos desbloqueados como vistos (al abrir el cofre). */
  async markAllSeen(email?: string | null): Promise<void> {
    const unlocked = [...(await this.getUnlockedAchievements(email))];
    this.writeLocalSeen(email, unlocked);
    if (!email) return;
    const progress = await this.ensureLoaded(email);
    const merged = new Set([...(progress.seen || []), ...unlocked]);
    if (merged.size === (progress.seen || []).length) return; // nada nuevo
    progress.seen = [...merged];
    await this.persist(email, progress);
  }
}

export const achievementService = new AchievementService();
export default achievementService;
